import { httpClient } from './httpClient';
import type {
  EnhancedCustomer,
  CustomerContact,
  CustomerAddress,
  CustomerSocialProfile,
  CustomerActivity,
  CustomerTag,
  CustomerWithRelations,
  CreateContactDto,
  UpdateContactDto,
  CreateAddressDto,
  UpdateAddressDto,
  CreateSocialProfileDto,
  UpdateSocialProfileDto,
  CreateActivityDto,
  UpdateActivityDto,
  CreateActivityAttachmentDto,
  CreateTagDto,
  UpdateTagDto,
  AssignTagDto,
  ActivityQueryParams,
  ActivityListResponse,
  UpdateCustomerDto
} from '../types/customer360';
import type {
  CustomFieldDefinition,
  CustomFieldDefinitionsResponse,
  CreateCustomFieldDefinitionDto,
  UpdateCustomFieldDefinitionDto,
  CustomerCustomFieldValue,
  SetCustomFieldValueDto,
  CustomFieldDefinitionsQueryParams,
  CustomFieldValuesQueryParams,
  CustomFieldsSearchQuery
} from '../types/customFields';

// Legacy Customer interface for backwards compatibility
export interface Customer {
  id: number;
  full_name: string;
  phone: string;
  external_ref?: string;
  created_at: string;
  updated_at: string;
  email?: string;
}

export interface CustomerQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CustomerResponse {
  customers: Customer[];
  total: number;
  totalPages: number;
  page?: number;
  limit?: number;
}

export interface CreateCustomerFormData {
  full_name: string;
  phone: string;
  external_ref?: string;
}

export interface CustomerStatistics {
  total_tickets: number;
  open_tickets: number;
  in_progress_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  low_priority: number;
  medium_priority: number;
  high_priority: number;
  urgent_priority: number;
  first_ticket_date: string | null;
  last_ticket_date: string | null;
  avg_resolution_hours: number | null;
}

export interface CustomerTicket {
  id: number;
  title: string;
  description: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  created_by_name: string;
  assigned_to_name: string | null;
}

export interface CustomerTicketsResponse {
  tickets: CustomerTicket[];
  total: number;
  totalPages: number;
  page: number;
  limit: number;
}

export const customerService = {
  createCustomer(data: CreateCustomerFormData): Promise<Customer> {
    return httpClient.post('/api/customers', data);
  },

  getCustomer(id: string | number): Promise<Customer> {
    return httpClient.get(`/api/customers/${id}`);
  },

  getCustomers(params?: CustomerQueryParams): Promise<Customer[] | CustomerResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    return httpClient.get(`/api/customers${queryString}`);
  },

  getCustomerByPhone(phoneNumber: string): Promise<Customer | null> {
    const queryParams = new URLSearchParams();
    queryParams.append('phoneNumber', phoneNumber);

    return httpClient.get(`/api/customers/find-by-phone?${queryParams.toString()}`);
  },

  updateCustomer(id: string | number, data: UpdateCustomerDto): Promise<EnhancedCustomer> {
    return httpClient.put(`/api/customers/${id}`, data);
  },

  deleteCustomer(id: number): Promise<void> {
    return httpClient.delete(`/api/customers/${id}`);
  },

  getCustomerWithStats(id: string | number): Promise<Customer & { statistics: CustomerStatistics }> {
    return httpClient.get(`/api/customers/${id}/stats`);
  },

  getCustomerTickets(id: string | number, params?: {
    page?: number;
    limit?: number;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<CustomerTicketsResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    return httpClient.get(`/api/customers/${id}/tickets${queryString}`);
  },

  getCustomerStatistics(id: string | number): Promise<CustomerStatistics> {
    return httpClient.get(`/api/customers/${id}/statistics`);
  },

  // ==================== Customer 360 Phase 1 Methods ====================

  // Contact Methods
  getCustomerContacts(customerId: number): Promise<CustomerContact[]> {
    return httpClient.get(`/api/customers/${customerId}/contacts`);
  },

  createContact(customerId: number, data: Omit<CreateContactDto, 'customer_id'>): Promise<CustomerContact> {
    return httpClient.post(`/api/customers/${customerId}/contacts`, data);
  },

  updateContact(contactId: number, data: UpdateContactDto): Promise<CustomerContact> {
    return httpClient.put(`/api/contacts/${contactId}`, data);
  },

  deleteContact(contactId: number): Promise<void> {
    return httpClient.delete(`/api/contacts/${contactId}`);
  },

  // Address Methods
  getCustomerAddresses(customerId: number): Promise<CustomerAddress[]> {
    return httpClient.get(`/api/customers/${customerId}/addresses`);
  },

  createAddress(customerId: number, data: Omit<CreateAddressDto, 'customer_id'>): Promise<CustomerAddress> {
    return httpClient.post(`/api/customers/${customerId}/addresses`, data);
  },

  updateAddress(addressId: number, data: UpdateAddressDto): Promise<CustomerAddress> {
    return httpClient.put(`/api/addresses/${addressId}`, data);
  },

  deleteAddress(addressId: number): Promise<void> {
    return httpClient.delete(`/api/addresses/${addressId}`);
  },

  // Social Profile Methods
  getCustomerSocialProfiles(customerId: number): Promise<CustomerSocialProfile[]> {
    return httpClient.get(`/api/customers/${customerId}/social-profiles`);
  },

  createSocialProfile(customerId: number, data: Omit<CreateSocialProfileDto, 'customer_id'>): Promise<CustomerSocialProfile> {
    return httpClient.post(`/api/customers/${customerId}/social-profiles`, data);
  },

  updateSocialProfile(profileId: number, data: UpdateSocialProfileDto): Promise<CustomerSocialProfile> {
    return httpClient.put(`/api/social-profiles/${profileId}`, data);
  },

  deleteSocialProfile(profileId: number): Promise<void> {
    return httpClient.delete(`/api/social-profiles/${profileId}`);
  },

  // Activity Timeline Methods
  getCustomerActivities(customerId: number, params?: ActivityQueryParams): Promise<ActivityListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.activity_type) queryParams.append('activity_type', params.activity_type);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return httpClient.get(`/api/customers/${customerId}/activities${queryString}`);
  },

  createActivity(customerId: number, data: Omit<CreateActivityDto, 'customer_id'>): Promise<CustomerActivity> {
    return httpClient.post(`/api/customers/${customerId}/activities`, data);
  },

  updateActivity(activityId: number, data: UpdateActivityDto): Promise<CustomerActivity> {
    return httpClient.put(`/api/activities/${activityId}`, data);
  },

  deleteActivity(activityId: number): Promise<void> {
    return httpClient.delete(`/api/activities/${activityId}`);
  },

  createActivityAttachment(activityId: number, data: Omit<CreateActivityAttachmentDto, 'activity_id'>): Promise<void> {
    return httpClient.post(`/api/activities/${activityId}/attachments`, data);
  },

  // Tag Methods
  async getAllTags(): Promise<CustomerTag[]> {
    const response = await httpClient.get('/api/customer-tags');
    return normalizeTagResponse(response);
  },

  createTag(data: CreateTagDto): Promise<CustomerTag> {
    return httpClient.post('/api/customer-tags', data);
  },

  updateTag(tagId: number, data: UpdateTagDto): Promise<CustomerTag> {
    return httpClient.put(`/api/customer-tags/${tagId}`, data);
  },

  deleteTag(tagId: number): Promise<void> {
    return httpClient.delete(`/api/customer-tags/${tagId}`);
  },

  async getCustomerTags(customerId: number): Promise<CustomerTag[]> {
    const response = await httpClient.get(`/api/customers/${customerId}/tags`);
    return normalizeTagResponse(response);
  },

  assignTag(customerId: number, data: Omit<AssignTagDto, 'customer_id'>): Promise<void> {
    return httpClient.post(`/api/customers/${customerId}/tags`, data);
  },

  unassignTag(customerId: number, tagId: number): Promise<void> {
    return httpClient.delete(`/api/customers/${customerId}/tags/${tagId}`);
  },

  // Customer 360 Full Profile
  getCustomerFull(customerId: number): Promise<CustomerWithRelations> {
    return httpClient.get(`/api/customers/${customerId}/full`);
  },

  // ==================== Custom Fields Methods ====================

  // Field Definitions Management (Admin only)
  getAllFieldDefinitions(params?: CustomFieldDefinitionsQueryParams): Promise<CustomFieldDefinitionsResponse> {
    const queryParams = new URLSearchParams();

    if (params?.field_type) queryParams.append('field_type', params.field_type);
    if (params?.field_group) queryParams.append('field_group', params.field_group);
    if (params?.is_visible !== undefined) queryParams.append('is_visible', params.is_visible.toString());
    if (params?.is_searchable !== undefined) queryParams.append('is_searchable', params.is_searchable.toString());
    if (params?.include_deleted !== undefined) queryParams.append('include_deleted', params.include_deleted.toString());

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return httpClient.get(`/api/custom-fields/field-definitions${queryString}`);
  },

  getFieldDefinitionById(fieldId: number): Promise<CustomFieldDefinition> {
    return httpClient.get(`/api/custom-fields/field-definitions/${fieldId}`);
  },

  createFieldDefinition(data: CreateCustomFieldDefinitionDto): Promise<CustomFieldDefinition> {
    return httpClient.post('/api/custom-fields/field-definitions', data);
  },

  updateFieldDefinition(fieldId: number, data: UpdateCustomFieldDefinitionDto): Promise<CustomFieldDefinition> {
    return httpClient.put(`/api/custom-fields/field-definitions/${fieldId}`, data);
  },

  deleteFieldDefinition(fieldId: number): Promise<void> {
    return httpClient.delete(`/api/custom-fields/field-definitions/${fieldId}`);
  },

  // Field Groups
  getFieldGroups(): Promise<string[]> {
    return httpClient.get('/api/custom-fields/field-groups');
  },

  // Customer Custom Field Values
  getCustomerFieldValues(customerId: number, params?: CustomFieldValuesQueryParams): Promise<CustomerCustomFieldValue[]> {
    const queryParams = new URLSearchParams();

    if (params?.field_definition_ids && params.field_definition_ids.length > 0) {
      params.field_definition_ids.forEach(id => queryParams.append('field_definition_ids[]', id.toString()));
    }
    if (params?.include_definitions !== undefined) {
      queryParams.append('include_definitions', params.include_definitions.toString());
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return httpClient.get(`/api/custom-fields/customers/${customerId}/custom-fields${queryString}`);
  },

  setCustomFieldValue(customerId: number, data: Omit<SetCustomFieldValueDto, 'customer_id'>): Promise<CustomerCustomFieldValue> {
    return httpClient.post(`/api/custom-fields/customers/${customerId}/custom-fields`, {
      ...data,
      customer_id: customerId
    });
  },

  bulkSetCustomFieldValues(customerId: number, values: { field_definition_id: number; value: any }[]): Promise<CustomerCustomFieldValue[]> {
    return httpClient.put(`/api/custom-fields/customers/${customerId}/custom-fields/bulk`, {
      customer_id: customerId,
      values
    });
  },

  deleteCustomFieldValue(customerId: number, fieldDefinitionId: number): Promise<void> {
    return httpClient.delete(`/api/custom-fields/customers/${customerId}/custom-fields/${fieldDefinitionId}`);
  },

  // Search customers by custom field values
  searchCustomersByCustomFields(searchQuery: CustomFieldsSearchQuery): Promise<number[]> {
    return httpClient.post('/api/custom-fields/customers/search/custom-fields', searchQuery);
  }
};

const normalizeTagResponse = (payload: any): CustomerTag[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload?.data) {
    if (Array.isArray(payload.data)) {
      return payload.data;
    }

    if (Array.isArray(payload.data.tags)) {
      return payload.data.tags;
    }
  }

  if (Array.isArray(payload?.tags)) {
    return payload.tags;
  }

  return [];
};
